import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Article as ArticleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const DocumentHandover = () => {
  const navigate = useNavigate();
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const documents = [
    {
      id: 0,
      name: 'Document Review Summary Report',
      status: 'Auto Generated',
      systemGenerated: true,
      generatedDate: new Date().toLocaleDateString(),
      previewUrl: '/path/to/summary.pdf'
    },
    {
      id: 1,
      name: 'Electronic Signature Consent',
      status: 'Ready',
      description: 'Consent for electronic signature process',
      previewUrl: '/path/to/document.pdf'
    },
    {
      id: 2,
      name: 'Insurance Company Consent',
      status: 'Ready',
      description: 'Consent for insurance company policies',
    },
    {
      id: 3,
      name: 'AB Form',
      status: 'Ready',
      description: 'Automobile Insurance Benefits Form',
    },
    {
      id: 4,
      name: 'PAC Form',
      status: 'Ready',
      description: 'Pre-Authorized Credit Form',
    },
    {
      id: 5,
      name: 'Application',
      status: 'Ready',
      description: 'Insurance application form',
    }
  ];

  const handleDocumentClick = (document) => {
    setSelectedDocument(document);
    setOpenPreviewDialog(true);
  };

  const handleSubmit = () => {
    navigate('/signature-confirm');
  };

  const handleDownload = (document) => {
    console.log('Downloading:', document.name);
  };

  return (
    <Container maxWidth="xl">
      {/* Hero Section */}
      <Box sx={{ 
        pt: 4,
        pb: 3,
        textAlign: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0e0e0',
        mb: 4
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/report')}
          startIcon={<ArrowBackIcon />}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Back to Report
        </Button>

        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 600,
            mb: 1
          }}
        >
          Document Summary
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Review and prepare documents for electronic signature
        </Typography>

        {/* Next Button */}
        <Button
          variant="contained"
          endIcon={<SendIcon />}
          onClick={handleSubmit}
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: '#1976d2',
            '&:hover': {
              backgroundColor: '#1565c0',
            },
          }}
        >
          Prepare for Signature
        </Button>
      </Box>

      {/*  Document Review Summary Report - Success Version */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ 
          p: 2.5,
          bgcolor: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          position: 'relative',
          '&:hover': {
            bgcolor: '#f8f9fa'
          }
        }}>
          {/* System Generated Badge */}
          <Chip
            label="System Generated"
            size="small"
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: '#e3f2fd',
              color: '#1565c0',
              fontWeight: 500,
              fontSize: '0.75rem'
            }}
          />

          {/* Report Content */}
          <Box sx={{ maxWidth: '80%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#e8f5e9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: '#1565c0', fontWeight: 500 }}>
                  Document Review Summary Report
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  All required documents have been verified and prepared for electronic signature
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 5.5 }}>
              Comprehensive analysis of all submitted documents including verification results and compliance checks
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 5.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Generated on {documents[0].generatedDate}
              </Typography>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#bdbdbd' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                PDF Format
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1.5,
            mt: 3,
            ml: 5.5
          }}>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => handleDocumentClick(documents[0])}
              size="small"
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              Preview Report
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(documents[0])}
              size="small"
              sx={{
                bgcolor: '#1976d2',
                '&:hover': {
                  bgcolor: '#1565c0'
                }
              }}
            >
              Download Report
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Document Review Summary Report - Warning Version */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ 
          p: 2.5,
          bgcolor: '#fff',
          border: '1px solid #ffb74d',
          borderRadius: 2,
          position: 'relative',
          '&:hover': {
            bgcolor: '#fff3e0'
          }
        }}>
          {/* Warning Badge */}
          <Chip
            label="Action Required"
            size="small"
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: '#fff3e0',
              color: '#e65100',
              fontWeight: 500,
              fontSize: '0.75rem'
            }}
          />

          {/* Report Content */}
          <Box sx={{ maxWidth: '80%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#fff3e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <WarningIcon sx={{ color: '#f57c00', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: '#e65100', fontWeight: 500 }}>
                  Document Review Summary Report
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Several issues require attention before proceeding with electronic signature
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 5.5 }}>
              The following items need to be addressed:
            </Typography>

            {/* Warning Items List */}
            <Box sx={{ ml: 5.5, mb: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Box sx={{ 
                    width: 6, 
                    height: 6, 
                    borderRadius: '50%', 
                    bgcolor: '#f57c00', 
                    mt: 1.5 
                  }} />
                  <Typography variant="body2" color="text.secondary">
                    Address Verification: The address on the application does not match the address shown on MVR or Auto+ reports
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Box sx={{ 
                    width: 6, 
                    height: 6, 
                    borderRadius: '50%', 
                    bgcolor: '#f57c00', 
                    mt: 1.5 
                  }} />
                  <Typography variant="body2" color="text.secondary">
                    Winter Tire Discount: Documentation required for winter tire installation
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Box sx={{ 
                    width: 6, 
                    height: 6, 
                    borderRadius: '50%', 
                    bgcolor: '#f57c00', 
                    mt: 1.5 
                  }} />
                  <Typography variant="body2" color="text.secondary">
                    Liability Coverage: Confirmation needed for declined $2,000,000 liability coverage
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 5.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Generated on {documents[0].generatedDate}
              </Typography>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#bdbdbd' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                PDF Format
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1.5,
            mt: 3,
            ml: 5.5
          }}>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => handleDocumentClick(documents[0])}
              size="small"
              sx={{
                borderColor: '#f57c00',
                color: '#f57c00',
                '&:hover': {
                  borderColor: '#e65100',
                  bgcolor: 'rgba(245, 124, 0, 0.04)'
                }
              }}
            >
              Preview Report
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(documents[0])}
              size="small"
              sx={{
                bgcolor: '#f57c00',
                '&:hover': {
                  bgcolor: '#e65100'
                }
              }}
            >
              Download Report
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Document List */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, color: '#1565c0' }}>
          Reviewed Documents
        </Typography>
        
        <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 2, overflow: 'hidden' }}>
          {/* Table Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 3,
            p: 2,
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            backgroundColor: '#fff'
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                Document Name
              </Typography>
            </Box>
            <Box sx={{ width: 100, textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                Actions
              </Typography>
            </Box>
          </Box>

          {/* Document List */}
          <List sx={{ p: 0 }}>
            {documents.map((doc, index) => (
              <React.Fragment key={doc.id}>
                <ListItem
                  sx={{
                    py: 2,
                    px: 2,
                    backgroundColor: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.01)',
                    },
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    width: '100%',
                    gap: 3
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1">
                        {doc.name}
                      </Typography>
                    </Box>

                    <Box sx={{ width: 100, display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleDocumentClick(doc)}
                        sx={{ color: '#1976d2' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </ListItem>
                {index < documents.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Paper>

      {/* Next Steps */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, color: '#1565c0' }}>
          Next Steps
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
          1. Click "Prepare for Signature" to proceed with the electronic signature process
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
          2. Review and confirm recipient information
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          3. Send documents for electronic signature
        </Typography>
      </Paper>

      {/* Document Preview Dialog */}
      <Dialog
        open={openPreviewDialog}
        onClose={() => setOpenPreviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6">
            {selectedDocument?.name}
          </Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(selectedDocument)}
              sx={{ mr: 2 }}
            >
              Download
            </Button>
            <IconButton
              size="small"
              onClick={() => setOpenPreviewDialog(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ minHeight: '70vh', p: 0 }}>
          {selectedDocument && (
            <Box sx={{ height: '100%', width: '100%', p: 2 }}>
              <iframe
                src={selectedDocument.previewUrl}
                style={{
                  width: '100%',
                  height: '70vh',
                  border: 'none',
                  backgroundColor: '#f5f5f5'
                }}
                title="Document Preview"
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default DocumentHandover; 